/** @format — Shipment-scoped documents page */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { ChevronRight } from "lucide-react";
import DocumentsToolbar from "./components/DocumentsToolbar";
import DocumentsList from "./components/DocumentsList";
import DocumentsListSkeleton from "./components/DocumentsListSkeleton";
import DocumentsStatusTabs from "./components/DocumentsStatusTabs";
import DocumentsAwaitingList from "./components/DocumentsAwaitingList";
import DocumentsMobileHeader from "./components/DocumentsMobileHeader";
import { useDocumentsList } from "./hooks/useDocumentsList";
import {
  useShipmentDocuments,
  type ShipmentDocumentTab,
} from "../shipments/hooks/useShipmentDocuments";
import type { CustomDateRange, DocumentDateFilter, DocumentItem, DocumentSortBy, DocumentSortOrder } from "./types";

export default function DocumentsShipmentPage({ onMenuClick }: { onMenuClick?: () => void }) {
  const navigate = useNavigate();
  const { listingId = "" } = useParams();
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [type, setType] = useState("all");
  const [dateFilter, setDateFilter] = useState<DocumentDateFilter | string>("all");
  const [customDateRange, setCustomDateRange] = useState<CustomDateRange>({});
  const [sortBy, setSortBy] = useState<DocumentSortBy>("date_created");
  const [sortOrder, setSortOrder] = useState<DocumentSortOrder>("newest");
  const [activeTab, setActiveTab] = useState<ShipmentDocumentTab>("received");
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const { documents, filteredCount, isPending } = useDocumentsList({
    search: debouncedSearch,
    type,
    dateFilter,
    customDateRange,
    sortBy,
    sortOrder,
    listingId,
  });

  const { received, expected, overdue, counts, isLoading: tabsLoading } =
    useShipmentDocuments(listingId);

  const title = useMemo(() => {
    const fromDoc = documents[0] ?? received[0];
    const shipmentLabel = listingId.slice(0, 8).toUpperCase();
    if (!fromDoc) return shipmentLabel;
    const vehicle = fromDoc.name?.split("-")[1]?.trim();
    return vehicle ? `${shipmentLabel} ${vehicle}` : shipmentLabel;
  }, [documents, received, listingId]);

  const handleOpen = (document: DocumentItem) => {
    const target = document.fileUrl || document.previewUrl;
    if (target) {
      window.open(target, "_blank", "noopener,noreferrer");
      return;
    }
    toast.error("No file available to open");
  };

  const countLabel = useMemo(() => {
    if (activeTab === "received") return `${filteredCount} items`;
    if (activeTab === "expected") {
      const receivedCount = expected.filter((item) => item.exists).length;
      const total = expected.length;
      return `${receivedCount} of ${total} documents received`;
    }
    const overdueCount = overdue.filter((item) => !item.exists).length;
    return `${overdueCount} documents overdue`;
  }, [activeTab, filteredCount, expected, overdue]);

  const contentClass =
    "flex min-h-0 flex-1 flex-col gap-4 overflow-hidden px-4 pb-6 pt-3 md:gap-4 md:px-5";

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white">
      <DocumentsMobileHeader onMenuClick={onMenuClick} />
      <div className={contentClass}>
        <div className="flex flex-col gap-4 py-2 md:py-4">
          <div className="flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={() => navigate("/documents?sort=listing")}
              className="flex items-center gap-1.5 text-sm font-medium text-neutral-700"
            >
              ← Back
            </button>
            <div className="flex items-center gap-1 text-sm">
              <Link to="/documents?sort=listing" className="text-neutral-500 hover:text-neutral-700">
                Documents
              </Link>
              <ChevronRight className="h-3.5 w-3.5 text-neutral-500" />
              <span className="text-[#0a0a0a]">{title}</span>
            </div>
          </div>
        </div>

        <div className="flex min-w-0 flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
          <DocumentsToolbar
            search={searchInput}
            onSearchChange={setSearchInput}
            type={type}
            onTypeChange={setType}
            dateFilter={dateFilter}
            customDateRange={customDateRange}
            onDateFilterChange={setDateFilter}
            onCustomDateRangeChange={setCustomDateRange}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortByChange={setSortBy}
            onSortOrderChange={setSortOrder}
            viewMode="list"
            onViewModeChange={() => {}}
            showViewToggle={false}
            showSearch={false}
          />
          <DocumentsStatusTabs activeTab={activeTab} onChange={setActiveTab} counts={counts} />
        </div>

        <p className="shrink-0 text-xs font-semibold uppercase text-[#344054]">{countLabel}</p>

        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto pb-4">
          {activeTab === "received" ? (
            isPending ? (
              <DocumentsListSkeleton />
            ) : (
              <DocumentsList
                documents={documents}
                onOpen={handleOpen}
                onGoToShipment={() => {}}
                showGoToShipment={false}
                onToggleDateSort={() =>
                  setSortOrder((current) => (current === "newest" ? "oldest" : "newest"))
                }
              />
            )
          ) : null}

          {activeTab === "expected" ? (
            tabsLoading ? (
              <DocumentsListSkeleton />
            ) : (
              <DocumentsAwaitingList
                items={expected}
                mode="expected"
                onViewDocument={(url) => window.open(url, "_blank", "noopener,noreferrer")}
              />
            )
          ) : null}

          {activeTab === "overdue" ? (
            tabsLoading ? (
              <DocumentsListSkeleton />
            ) : (
              <DocumentsAwaitingList items={overdue} mode="overdue" />
            )
          ) : null}
        </div>
      </div>
    </div>
  );
}
